import React from 'react';

import {Icon} from '../../components/Icon';
import {WBPopup, type WBPopupDirection} from '../legacy-wandb-ui/WBPopup';
import {useOnMouseDownOutside} from '../util/dom';
import {
  getOptionDisplayName,
  WBMenu,
  type WBMenuOnSelectHandler,
  type WBMenuOption,
  type WBMenuTheme,
} from './WBMenu';
import * as S from './WBSelect.styles';

export type WBSelectProps = {
  className?: string;
  popupStyle?: React.CSSProperties;
  value: string | number;
  displayedValue?: string | number;
  options: WBMenuOption[];
  typeable?: boolean;
  menuWidth?: number;
  menuTheme?: WBMenuTheme;
  menuBackgroundColor?: string;
  menuFontSize?: number;
  menuLineHeight?: number;
  autoMenuWidth?: boolean;
  disabled?: boolean;
  // Direction the popup menu opens relative to the trigger. Defaults to
  // 'center right', which vertically centers the menu on the selected option.
  // Pass e.g. 'top right' for triggers near the bottom of the viewport so the
  // menu opens upward and all options stay on-screen.
  popupDirection?: WBPopupDirection;
  onSelect: WBMenuOnSelectHandler;
  onChangeHoveredIndex?: (index: number) => void;
  'data-test'?: string;
};

export const WBSelect: React.FC<WBSelectProps> = props => {
  const [rect, setRect] = React.useState<DOMRect | undefined>();
  const [open, setOpen] = React.useState(false);
  const [inputFocused, setInputFocused] = React.useState(false);
  const [defaultWidth, setDefaultWidth] = React.useState(0);
  const [selectedElement, setSelectedElement] =
    React.useState<HTMLDivElement | null>(null);

  const [openerEl, setOpenerEl] = React.useState<HTMLDivElement | null>(null);
  const [popupEl, setPopupEl] = React.useState<HTMLDivElement | null>(null);
  const onClickOutside = React.useCallback(() => {
    setOpen(false);
  }, []);
  const mousedownExcludedEls = React.useMemo(
    () => [openerEl, popupEl],
    [openerEl, popupEl]
  );
  useOnMouseDownOutside(mousedownExcludedEls, onClickOutside);

  // When a caller pins the popup direction, anchor to the trigger itself
  // rather than centering the menu on the selected option. This keeps the
  // whole menu on-screen for triggers near a viewport edge (e.g. the run
  // selector pagination at the bottom of the page).
  const anchorToTrigger = props.popupDirection != null;
  const popupDirection = props.popupDirection ?? 'center right';

  let menuX = 0;
  let menuY = 0;
  if (rect != null) {
    menuX = rect.left;
    menuY = anchorToTrigger ? rect.top : rect.top + rect.height / 2;
  }

  const selectedOption = props.options.find(opt => opt.value === props.value);
  const displayedValue =
    props.displayedValue ??
    (selectedOption == null
      ? props.value
      : getOptionDisplayName(selectedOption));

  return (
    <>
      {props.typeable ? (
        <S.TypeableWrapper
          data-test={props['data-test']}
          open={open && !props.disabled}
          inputFocused={inputFocused && !props.disabled}
          className={props.className}
          onMouseDown={e => {
            setRect(e.currentTarget.getBoundingClientRect());
            setDefaultWidth(e.currentTarget.offsetWidth);
          }}>
          <S.StyledAutoCompletingInput
            options={props.options.map(opt => getOptionDisplayName(opt))}
            value={selectedOption?.name}
            disabled={props.disabled}
            onSelect={val => {
              const selected = props.options.find(
                opt => getOptionDisplayName(opt) === val
              );
              if (selected != null) {
                props.onSelect(selected.value, {option: selected});
              }
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
          />
          <S.CaretWrapper
            ref={setOpenerEl}
            onMouseDown={() => setOpen(o => !o)}>
            <Icon name="chevron-next" />
          </S.CaretWrapper>
        </S.TypeableWrapper>
      ) : (
        <S.BasicWrapper
          data-test={props['data-test']}
          // Pass the state setter itself as the ref. An inline arrow function
          // would be a new function on every render, which makes React detach
          // and re-attach the ref on every commit.
          ref={setOpenerEl}
          tabIndex={props.disabled ? undefined : 0}
          // onMouseDown={e => e.preventDefault()}
          // onFocus={e => {
          //   setRect(e.currentTarget.getBoundingClientRect());
          //   setOpen(true);
          // }}
          // onBlur={() => {
          //   setOpen(false);
          // }}
          onKeyDown={e => {
            if (e.keyCode === 38 || e.keyCode === 40 /* up or down */) {
              if (!open) {
                setRect(e.currentTarget.getBoundingClientRect());
                setDefaultWidth(e.currentTarget.offsetWidth);
                setOpen(true);
              }
            }
          }}
          open={open && !props.disabled}
          onMouseDown={e => {
            setRect(e.currentTarget.getBoundingClientRect());
            setDefaultWidth(e.currentTarget.offsetWidth);
            setOpen(o => !o);
          }}
          className={props.className}>
          <S.DisplayedValue>{displayedValue}</S.DisplayedValue>
          <S.DropdownArrow />
        </S.BasicWrapper>
      )}
      {open && !props.disabled && (
        <WBPopup
          style={props.popupStyle}
          x={menuX}
          y={menuY}
          direction={popupDirection}
          ref={setPopupEl}
          elementToPosition={anchorToTrigger ? undefined : selectedElement}>
          <WBMenu
            theme={props.menuTheme}
            backgroundColor={props.menuBackgroundColor}
            fontSize={props.menuFontSize}
            lineHeight={props.menuLineHeight}
            width={
              props.autoMenuWidth
                ? undefined
                : (props.menuWidth ?? defaultWidth)
            }
            selected={props.value}
            options={props.options}
            onSelect={(val, extra) => {
              setOpen(false);
              props.onSelect(val, extra);
            }}
            onEsc={() => {
              setOpen(false);
            }}
            selectedRef={setSelectedElement}
          />
        </WBPopup>
      )}
    </>
  );
};
